/**
 * Phone number sign-in (design "Phone number sign-in", docs/features/account-sign-in.md → "Phone number"):
 * - PhoneNumberSteps: the number (country, field, the Google / Slack pre-fill chip, the proof when one is needed) and
 *   then the texted code (Resend after 30 s, or a call). Settings' phone sheet and sign-up's "Verify your phone"
 *   (A / B / C) both render it over AccountSecurityViewModel's phone sheet.
 * - TextMeOffer: the email code step's "Can't get the code? Text me a code instead" (D).
 * - PhoneSignInStep: "Check your phone" (E), SessionViewModel's phoneCode flow.
 */
import { Phone, type AccountSecurityState, type AccountSecurityViewModel, type SessionViewModel, type SignInBusy, type SignInFlow } from "@bundle/core";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Banner, Button, Chip, CodeCells, Icon, Menu, Pressable, Stack, Text, TextField } from "../primitives/index.ts";
import { useLocale, useT, useTr } from "../i18n/I18nProvider.tsx";

/** Seconds left until `at` (epoch ms), ticking once a second while above zero. */
function useSecondsUntil(at: number | undefined): number {
  const left = () => Phone.resendLeft(at, Date.now());
  const [s, setS] = useState(left);
  useEffect(() => {
    setS(left());
    if (at === undefined) return;
    const id = setInterval(() => {
      const n = left();
      setS(n);
      if (n === 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [at]);
  return s;
}

function useCountryName(): (cc: string) => string {
  const locale = useLocale();
  return useMemo(() => {
    let names: Intl.DisplayNames | undefined;
    try {
      names = new Intl.DisplayNames([locale], { type: "region" });
    } catch {
      names = undefined;
    }
    return (cc: string) => names?.of(cc) ?? cc;
  }, [locale]);
}

export type PhoneNumberStepsProps = {
  vm: AccountSecurityViewModel;
  state: AccountSecurityState;
  /** The proof fields (password / emailed code) for a change that needs one; absent on a fresh sign-up. */
  proof?: ReactNode;
  /** Sign-up: "Skip for now" (absent when the number is required, and in Settings). */
  onSkip?: () => void;
  /** Settings: Cancel next to the main button. */
  cancel?: ReactNode;
  /** The platform can read the number from Google ("Use the number from my Google account"). */
  google?: boolean;
};

/** The phone sheet's two steps: the number, then the code texted (or read out in a call) to it. */
export function PhoneNumberSteps({ vm, state: s, proof, onSkip, cancel, google = false }: PhoneNumberStepsProps) {
  const t = useT();
  const tr = useTr();
  const nameOf = useCountryName();
  const sheet = s.sheet?.kind === "phone" ? s.sheet : undefined;
  const resendIn = useSecondsUntil(sheet?.resendAt);
  if (sheet === undefined) return null;
  const error = s.error === undefined ? null : <Banner tone="danger" message={tr(s.error)} testID="phone-error" />;

  if (sheet.step === "code") {
    const verify = () => void vm.verifyPhone();
    const waiting = resendIn > 0;
    return (
      <Stack gap="4" testID="phone-code-step">
        <Text color="text.secondary">{t(sheet.channel === "voice" ? "phone.calledTo" : "phone.codeSentTo", { phone: sheet.sentTo ?? "" })}</Text>
        <CodeCells
          value={sheet.code}
          onChange={(v) => vm.setPhoneCode(v)}
          onComplete={() => verify()}
          label={t("phone.codeLabel")}
          cellLabel={(n) => t("auth.twoStep.digit", { n })}
          error={s.error !== undefined}
          disabled={s.busy}
          autoFocus
          testID="phone-code"
        />
        {error}
        <Button label={t("phone.verify")} variant="primary" size="lg" loading={s.busy} disabled={sheet.code.length !== 6} onPress={verify} testID="phone-verify" />
        <Stack align="center" gap="0.5">
          {waiting ? (
            <Text variant="footnote" color="text.tertiary" testID="phone-resend-in">
              {t("phone.resendIn", { time: Phone.countdown(resendIn) })}
            </Text>
          ) : (
            <Button label={t("phone.resend")} variant="ghost" size="sm" disabled={s.busy} onPress={() => void vm.sendPhoneCode("sms")} testID="phone-resend" />
          )}
          {s.phoneVoice ? <Button label={t("phone.callMe")} variant="ghost" size="sm" disabled={waiting || s.busy} onPress={() => void vm.sendPhoneCode("voice")} testID="phone-call" /> : null}
          <Button label={t("phone.changeNumber")} variant="ghost" size="sm" disabled={s.busy} onPress={() => vm.editPhoneNumber()} testID="phone-change-number" />
        </Stack>
      </Stack>
    );
  }

  const send = () => void vm.sendPhoneCode();
  const countries = s.phoneCountries.map((cc) => ({
    id: cc,
    label: `${Phone.flagOf(cc)}  ${nameOf(cc)}  ${Phone.dialCode(cc)}`,
    selected: cc === sheet.country,
    onSelect: () => vm.setPhoneCountry(cc),
  }));
  const source = sheet.prefill;
  return (
    <Stack gap="4" testID="phone-number-step">
      {proof}
      <Stack gap="2">
        {source === undefined ? null : <Chip label={t(source === "google" ? "phone.fromGoogle" : "phone.fromSlack")} icon="phone" testID="phone-prefill" />}
        <Stack direction="horizontal" gap="2" align="end">
          <Menu items={countries} testID="phone-country-menu">
            <Pressable onPress={() => {}} disabled={s.busy} accessibilityLabel={`${t("phone.country")}: ${nameOf(sheet.country)}`} radius="md" testID="phone-country">
              <Stack direction="horizontal" align="center" gap="1.5" paddingX="3" paddingY="2" radius="md" border="border.strong">
                <Text>{`${Phone.flagOf(sheet.country)} ${Phone.dialCode(sheet.country)}`}</Text>
                <Icon name="chevronDown" size="icon.sm" color="text.tertiary" />
              </Stack>
            </Pressable>
          </Menu>
          <Stack grow>
            <TextField
              label={t("phone.number")}
              value={sheet.number}
              onChange={(v) => vm.setPhoneNumber(v)}
              keyboard="phone"
              autoFocus={proof === undefined}
              disabled={s.busy}
              onSubmit={send}
              testID="phone-number"
            />
          </Stack>
        </Stack>
        <Text variant="caption" color="text.tertiary">
          {t("phone.rates")}
        </Text>
      </Stack>
      {error}
      <Stack direction="horizontal" gap="2" justify={cancel === undefined ? "start" : "end"}>
        {cancel}
        <Stack grow={cancel === undefined}>
          <Button label={t("phone.sendCode")} variant="primary" size="lg" loading={s.busy} disabled={!vm.phoneReady} onPress={send} testID="phone-send" />
        </Stack>
      </Stack>
      <Stack align="center" gap="0.5">
        {source !== undefined ? <Button label={t("phone.otherNumber")} variant="ghost" size="sm" onPress={() => vm.editPhoneNumber(true)} testID="phone-other-number" /> : null}
        {google && source !== "google" ? (
          <Button label={t("phone.importGoogle")} brand="google" variant="ghost" size="sm" loading={s.importing === true} onPress={() => void vm.importGooglePhone()} testID="phone-import-google" />
        ) : null}
        {onSkip === undefined ? null : <Button label={t("phone.skip")} variant="ghost" size="sm" onPress={onSkip} testID="phone-skip" />}
      </Stack>
    </Stack>
  );
}

/** D · The email code step's "Can't get the code?": a text to the account's number (shown masked). */
export function TextMeOffer({ vm, phone, busy }: { vm: SessionViewModel; phone: string; busy: SignInBusy | undefined }) {
  const t = useT();
  return (
    <Stack gap="2" testID="sign-in-text-offer">
      <Text variant="footnote" weight="semibold" color="text.secondary">
        {t("phone.cantGetCode")}
      </Text>
      <Pressable onPress={() => void vm.textMeCode()} disabled={busy !== undefined} accessibilityLabel={`${t("phone.textMe")}, ${t("phone.textMeTo", { phone })}`} testID="sign-in-text-me">
        <Stack direction="horizontal" align="center" gap="3" padding="3" radius="lg" border="border.subtle">
          <Icon name="phone" size="icon.md" color="text.secondary" />
          <Stack grow gap="0.5">
            <Text weight="semibold">{t("phone.textMe")}</Text>
            <Text variant="footnote" color="text.secondary">
              {t("phone.textMeTo", { phone })}
            </Text>
          </Stack>
          <Icon name="chevronRight" size="icon.sm" color="text.tertiary" />
        </Stack>
      </Pressable>
    </Stack>
  );
}

/** E · "Check your phone": the texted code signs in (two-step, when on, comes next). */
export function PhoneSignInStep({ vm, flow, busy, error }: { vm: SessionViewModel; flow: Extract<SignInFlow, { step: "phoneCode" }>; busy: SignInBusy | undefined; error: string | undefined }) {
  const t = useT();
  const [code, setCode] = useState("");
  const resendIn = useSecondsUntil(flow.resendAt);
  const verifying = busy === "verifying";
  const waiting = resendIn > 0;
  return (
    <Stack gap="4" testID="sign-in-phone-step">
      <Stack gap="1">
        <Text variant="headline">{t("phone.signInTitle")}</Text>
        <Text color="text.secondary">{t(flow.channel === "voice" ? "phone.calledTo" : "phone.signInBody", { phone: flow.phone })}</Text>
      </Stack>
      <CodeCells
        value={code}
        onChange={setCode}
        onComplete={(c) => void vm.verifyPhoneCode(c)}
        label={t("phone.codeLabel")}
        cellLabel={(n) => t("auth.twoStep.digit", { n })}
        error={error !== undefined}
        disabled={verifying}
        autoFocus
        testID="sign-in-phone-code"
      />
      {error === undefined ? null : <Banner tone="danger" message={error} testID="sign-in-error" />}
      <Button label={t("auth.continue")} variant="primary" size="lg" loading={verifying} disabled={code.length !== 6} onPress={() => void vm.verifyPhoneCode(code)} testID="sign-in-phone-submit" />
      <Text variant="caption" color="text.tertiary">
        {t("phone.twoStepNext")}
      </Text>
      <Stack align="center" gap="0.5">
        {waiting ? (
          <Text variant="footnote" color="text.tertiary">
            {t("phone.resendIn", { time: Phone.countdown(resendIn) })}
          </Text>
        ) : (
          <Button label={t("phone.resend")} variant="ghost" size="sm" loading={busy === "sendingCode"} onPress={() => void vm.textMeCode("sms")} testID="sign-in-phone-resend" />
        )}
        {flow.voice ? <Button label={t("phone.callMe")} variant="ghost" size="sm" disabled={waiting || busy !== undefined} onPress={() => void vm.textMeCode("voice")} testID="sign-in-phone-call" /> : null}
        <Button label={t("phone.tryOther")} variant="ghost" size="sm" onPress={() => vm.leavePhoneCode()} testID="sign-in-phone-other" />
      </Stack>
    </Stack>
  );
}
